import React from 'react';
import { useNavigate } from 'react-router-dom';
import TabMessagerie from '../../../composants/TabMessagerie/TabMessagerie';
import CommandeEvaluationTab from '../../../composants/AvisPrestataire/CommandeEvaluationTab';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import { OperationMS, StatusId, TabDescriptor } from '../types';
import { questionsReponsesNextTab, messagerieNextTab } from '../domain/nextTab';
import { MessageChannel } from '../hooks/useUnreadCounts';
import { getRetenuCommande } from '../domain/marche/marcheAttribution';
import { getSignatureAcheteurInfo } from '../domain/analyse/historique';
import { formatDateHeure } from '../domain/analyse/dates';
import OperationConsultation from './OperationConsultation';
import OperationAnalyse from './OperationAnalyse';
import OperationMarche from './OperationMarche';
import OperationHistoriqueMarche from './OperationHistoriqueMarche';
import OperationStatusBanner from './OperationStatusBanner';

interface OperationContentProps {
  operation: OperationMS;
  setOperation: React.Dispatch<React.SetStateAction<OperationMS | null>>;
  activeTab: TabDescriptor | null;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  refetch: () => void;
  onMarkRead?: (channel: MessageChannel) => void;
  printRef: React.RefObject<HTMLElement>;
}

// Routeur d'onglet par {kind} (jamais par index).
const OperationContent: React.FC<OperationContentProps> = ({
  operation,
  setOperation,
  activeTab,
  errors,
  setErrors,
  refetch,
  onMarkRead,
  printRef,
}) => {
  const navigate = useNavigate();
  if (!activeTab) return null;

  switch (activeTab.kind) {
    case 'historique':
      // Onglet Historique MS-acheteur, fork autonome (plus de composant partage).
      return <OperationHistoriqueMarche operation={operation} />;
    case 'consultation':
      return (
        <>
          <OperationStatusBanner operation={operation} context="consultation" />
          <OperationConsultation
            operation={operation}
            setOperation={setOperation}
            errors={errors}
            setErrors={setErrors}
            refetch={refetch}
            printRef={printRef}
          />
        </>
      );
    case 'questions_reponses': {
      const qrCta = questionsReponsesNextTab(operation);
      return (
        <TabMessagerie
          operationId={operation.id}
          operationStatus={operation.status?.id ?? 0}
          channel="questions_reponses"
          onMarkRead={onMarkRead}
          // Fin de consultation = date limite de remise (figee ; expire_at est nullee apres coup).
          consultationEndedSince={formatDateHeure(operation.date_limite_remise ?? operation.expire_at)}
          nextTabButton={
            <NextTabButton
              label={qrCta.label}
              hidden={qrCta.hidden}
              onClick={() => navigate({ search: `?tab=${qrCta.target}` })}
            />
          }
        />
      );
    }
    case 'analyse':
      return (
        <>
          <OperationStatusBanner operation={operation} context="analyse" />
          <OperationAnalyse
            operation={operation}
            setOperation={setOperation}
            refetch={refetch}
            printRef={printRef}
          />
        </>
      );
    case 'notifications':
      return <OperationMarche operation={operation} refetch={refetch} printRef={printRef} />;
    case 'messagerie': {
      const messagerieCta = messagerieNextTab(operation);
      // Messagerie active = marche signe ; date d'activation = date de signature acheteur.
      const signedMsg = operation.status?.id === StatusId.Valider;
      const messagerieActiveSince = signedMsg
        ? formatDateHeure(getSignatureAcheteurInfo(operation.historique).date)
        : undefined;
      return (
        <TabMessagerie
          operationId={operation.id}
          operationStatus={operation.status?.id ?? 0}
          channel="messagerie"
          onMarkRead={onMarkRead}
          messagerieActiveSince={messagerieActiveSince}
          nextTabButton={
            <NextTabButton
              label={messagerieCta.label}
              hidden={messagerieCta.hidden}
              onClick={() => navigate({ search: `?tab=${messagerieCta.target}` })}
            />
          }
        />
      );
    }
    case 'evaluation': {
      // Avis cree en amont (hook a la signature + backfill) ; lecture seule ici.
      // Cle de lecture = le retenu (commande == operation.id_commande), une fois l'op signee (11).
      const retenu = getRetenuCommande(operation);
      const signed = operation.status?.id === StatusId.Valider;
      return (
        <>
          <CommandeEvaluationTab
            typeCommande="MS"
            commandeId={retenu?.id ?? 0}
            validated={signed && Boolean(retenu?.id)}
          />
          <CommandeEvaluationTab
            typeCommande="MS"
            commandeId={retenu?.id ?? 0}
            validated={signed && Boolean(retenu?.id)}
            cible="acheteur"
          />
        </>
      );
    }
    default:
      return null;
  }
};

export default OperationContent;
